import { notificationDesign } from '../../utils/notificationDesign';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { App as AntApp } from "antd";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import normalNotificationSound from "../../assets/sounds/wati_notification.wav";
import criticalNotificationSound from "../../assets/sounds/urgent_notification.wav";
import {
  completeInternalUpload,
  ensureDirectConversation,
  fetchConversationMessages,
  fetchConversations,
  fetchInternalChatUsers,
  getInternalFileDownload,
  initiateInternalUpload,
  markConversationDelivered,
  markConversationRead,
  putSignedUpload,
  sendInternalMessage,
  syncInternalMessages,
} from "./internalChatApi";
import {
  applyReceipt,
  conversationTitle,
  highestIncomingMessageId,
  sortConversations,
  unreadMessageTotal,
  upsertMessages,
  userDisplayName,
} from "./internalChatModel";
import { useAccess } from "../../features/access/context/AccessProvider";
import { showNotification } from "../misc_functions";
import uzdLogo from "../../assets/images/uzd_logo_small.jpeg";

const InternalChatContext = createContext(null);
const PAGE_SIZE = 50;

export function useInternalChat() {
  const value = useContext(InternalChatContext);
  if (!value) throw new Error("useInternalChat must be used within InternalChatProvider");
  return value;
}

function accessDisabled(error) {
  return error?.response?.status === 403 && error?.response?.data?.data?.code === "CHAT_ACCESS_DISABLED";
}

function playSound(src) {
  try {
    const audio = new Audio(src);
    audio.volume = 0.8;
    audio.play().catch(() => {});
  } catch (_) { }
}

export function InternalChatProvider({ children }) {
  const { authState, updateUser } = useAuth();
  const { socket, isConnected } = useSocket();
  const { can, status: accessStatus } = useAccess();
  const { notification } = AntApp.useApp();
  const currentUserId = Number(authState?.id);

  const [enabled, setEnabled] = useState(null);
  const [initializing, setInitializing] = useState(false);
  const [users, setUsers] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [messageMeta, setMessageMeta] = useState({});
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState({});

  const usersRef = useRef(users);
  const conversationsRef = useRef(conversations);
  const messagesRef = useRef(messagesByConversation);
  const selectedRef = useRef(selectedConversationId);
  const openRef = useRef(isOpen);
  const enabledRef = useRef(enabled);
  const syncRunningRef = useRef(false);
  const refreshTimerRef = useRef(null);
  const lastConnectionRef = useRef(false);
  const deliveredWatermarkRef = useRef({});
  const readWatermarkRef = useRef({});

  useEffect(() => { usersRef.current = users; }, [users]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useEffect(() => { messagesRef.current = messagesByConversation; }, [messagesByConversation]);
  useEffect(() => { selectedRef.current = selectedConversationId; }, [selectedConversationId]);
  useEffect(() => { openRef.current = isOpen; }, [isOpen]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  const lastMessageStorageKey = currentUserId ? `internal_chat_last_message_id:${currentUserId}` : null;

  const disableChat = useCallback(() => {
    enabledRef.current = false;
    setEnabled(false);
    setIsOpen(false);
    setSelectedConversationId(null);
    setConversations([]);
    setMessagesByConversation({});
    setMessageMeta({});
    if (authState?.user?.internal_chat_enabled !== 0) updateUser({ internal_chat_enabled: 0 });
  }, [authState?.user?.internal_chat_enabled, updateUser]);

  const handleApiError = useCallback((error) => {
    if (accessDisabled(error)) disableChat();
    return error;
  }, [disableChat]);

  const recordLastMessageId = useCallback((id) => {
    if (!lastMessageStorageKey) return;
    const numeric = Number(id);
    if (!Number.isFinite(numeric)) return;
    const previous = Number(localStorage.getItem(lastMessageStorageKey) || 0);
    if (numeric > previous) localStorage.setItem(lastMessageStorageKey, String(numeric));
  }, [lastMessageStorageKey]);

  const mergeMessages = useCallback((conversationId, incoming) => {
    if (!conversationId || !incoming?.length) return;
    messagesRef.current = {
      ...messagesRef.current,
      [conversationId]: upsertMessages(messagesRef.current[conversationId] || [], incoming),
    };
    setMessagesByConversation(previous => ({
      ...previous,
      [conversationId]: upsertMessages(previous[conversationId] || [], incoming),
    }));
    incoming.forEach(message => recordLastMessageId(message.id));
  }, [recordLastMessageId]);

  const markDeliveredThrough = useCallback(async (conversationId, messages) => {
    const through = highestIncomingMessageId(messages, currentUserId);
    if (!through) return;
    const previous = Number(deliveredWatermarkRef.current[conversationId] || 0);
    if (through <= previous) return;
    deliveredWatermarkRef.current[conversationId] = through;
    try {
      await markConversationDelivered(conversationId, through);
    } catch (error) {
      deliveredWatermarkRef.current[conversationId] = previous;
      handleApiError(error);
    }
  }, [currentUserId, handleApiError]);

  const refreshUsers = useCallback(async () => {
    try {
      const rows = await fetchInternalChatUsers();
      setUsers(rows);
      const me = rows.find(user => Number(user.id) === currentUserId);
      // The login response is the initial access authority. A missing row or
      // soft-failed users response must not silently revoke an enabled session.
      if (!me) return enabledRef.current === true;
      const hasAccess = can("internal_chat.use") && Number(me?.approved) === 1 && Number(me?.internal_chat_enabled) === 1;
      enabledRef.current = hasAccess;
      setEnabled(hasAccess);
      if (me && Number(authState?.user?.internal_chat_enabled) !== Number(me.internal_chat_enabled)) {
        updateUser({
          internal_chat_enabled: me.internal_chat_enabled,
          internal_chat_last_seen_at: me.internal_chat_last_seen_at,
        });
      }
      if (!hasAccess) setIsOpen(false);
      return hasAccess;
    } catch (error) {
      handleApiError(error);
      return false;
    }
  }, [authState?.user?.internal_chat_enabled, currentUserId, handleApiError, updateUser, can]);

  const refreshConversations = useCallback(async () => {
    if (!currentUserId || enabledRef.current === false) return [];
    try {
      const rows = sortConversations(await fetchConversations());
      setConversations(rows);
      for (const conversation of rows) {
        const last = conversation.last_message;
        const incoming = last && (
          last.sender_type === "system" || Number(last.sender_user_id) !== currentUserId
        );
        if (incoming && !last.delivered_at) {
          markConversationDelivered(conversation.id, last.id).catch(handleApiError);
        }
      }
      return rows;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  }, [currentUserId, handleApiError]);

  const scheduleConversationRefresh = useCallback(() => {
    clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => refreshConversations(), 120);
  }, [refreshConversations]);

  useEffect(() => () => clearTimeout(refreshTimerRef.current), []);

  const loadMessages = useCallback(async (conversationId, { older = false } = {}) => {
    if (!conversationId) return [];
    const existing = messagesRef.current[conversationId] || [];
    const canonical = existing.filter(message => Number.isFinite(Number(message.id)));
    const beforeId = older && canonical.length ? canonical[0].id : null;
    setMessageMeta(previous => ({
      ...previous,
      [conversationId]: {
        ...(previous[conversationId] || {}),
        [older ? "loadingOlder" : "loading"]: true,
      },
    }));
    try {
      const rows = await fetchConversationMessages(conversationId, {
        limit: PAGE_SIZE,
        ...(beforeId ? { before_id: beforeId } : {}),
      });
      mergeMessages(conversationId, rows);
      setMessageMeta(previous => ({
        ...previous,
        [conversationId]: {
          ...(previous[conversationId] || {}),
          loading: false,
          loadingOlder: false,
          initialized: true,
          hasMore: rows.length === PAGE_SIZE,
        },
      }));
      markDeliveredThrough(conversationId, rows);
      return rows;
    } catch (error) {
      handleApiError(error);
      setMessageMeta(previous => ({
        ...previous,
        [conversationId]: { ...(previous[conversationId] || {}), loading: false, loadingOlder: false },
      }));
      return [];
    }
  }, [handleApiError, markDeliveredThrough, mergeMessages]);

  const syncMissedMessages = useCallback(async () => {
    if (!currentUserId || enabledRef.current === false || syncRunningRef.current) return;
    syncRunningRef.current = true;
    setSyncing(true);
    try {
      let afterId = Number(localStorage.getItem(lastMessageStorageKey) || 0);
      let hasMore = true;
      while (hasMore) {
        // eslint-disable-next-line no-await-in-loop
        const page = await syncInternalMessages(afterId, 200);
        const grouped = new Map();
        for (const message of page.messages || []) {
          const key = message.conversation_id;
          if (!grouped.has(key)) grouped.set(key, []);
          grouped.get(key).push(message);
        }
        grouped.forEach((messages, conversationId) => {
          mergeMessages(conversationId, messages);
          markDeliveredThrough(conversationId, messages);
        });
        afterId = Number(page.next_after_id || afterId);
        if (lastMessageStorageKey) localStorage.setItem(lastMessageStorageKey, String(afterId));
        hasMore = !!page.has_more;
      }
      await refreshConversations();
    } catch (error) {
      handleApiError(error);
    } finally {
      syncRunningRef.current = false;
      setSyncing(false);
    }
  }, [currentUserId, handleApiError, lastMessageStorageKey, markDeliveredThrough, mergeMessages, refreshConversations]);

  useEffect(() => {
    if (!currentUserId || !authState?.isAuthenticated) {
      setEnabled(null);
      setUsers([]);
      setConversations([]);
      setMessagesByConversation({});
      setIsOpen(false);
      return;
    }
    // Chat access is part of the authenticated session contract. Older
    // locally-persisted sessions do not contain it and must sign in again;
    // this also prevents briefly exposing the launcher before access is known.
    if (accessStatus !== "ready" || !can("internal_chat.use") || Number(authState?.user?.internal_chat_enabled) !== 1) {
      enabledRef.current = false;
      setEnabled(false);
      setIsOpen(false);
      return;
    }
    enabledRef.current = true;
    setEnabled(true);
    let cancelled = false;
    setInitializing(true);
    refreshUsers().then(async hasAccess => {
      if (cancelled || !hasAccess) return;
      await refreshConversations();
      if (!cancelled) await syncMissedMessages();
    }).finally(() => { if (!cancelled) setInitializing(false); });
    return () => { cancelled = true; };
  }, [authState?.isAuthenticated, authState?.user?.internal_chat_enabled, currentUserId, accessStatus, can]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isConnected && !lastConnectionRef.current && enabled === true) syncMissedMessages();
    lastConnectionRef.current = isConnected;
  }, [enabled, isConnected, syncMissedMessages]);

  const showIncomingNotification = useCallback((message) => {
    const incoming = Number(message.sender_user_id) !== currentUserId || message.sender_type === "system";
    if (!incoming) return;
    const visibleConversation = openRef.current
      && String(selectedRef.current) === String(message.conversation_id)
      && document.visibilityState === "visible";
    const priority = Number(message.priority || 3);
    if (priority === 5) playSound(criticalNotificationSound);
    else if (priority === 4 && !visibleConversation) playSound(normalNotificationSound);
    if (visibleConversation) return;

    const conversation = conversationsRef.current.find(row => String(row.id) === String(message.conversation_id));
    const sender = usersRef.current.find(user => Number(user.id) === Number(message.sender_user_id));
    const title = message.sender_type === "system"
      ? "SRM System"
      : userDisplayName(sender || conversation?.other_user);
    const description = message.text_content
      || (message.message_type === "image" ? "Sent an image" : "Sent a document");
    const config = {
      ...notificationDesign('internal', priority === 5 ? 'error' : priority === 4 ? 'warning' : 'info'),
      key: `internal-chat-${message.id || message.local_message_id}`,
      message: priority >= 4 ? `${priority === 5 ? "Critical" : "High priority"} message from ${title}` : title,
      description,
      duration: priority === 5 ? 0 : priority === 4 ? 8 : 5,
      placement: "topRight",
      onClick: () => {
        setIsOpen(true);
        setSelectedConversationId(message.conversation_id);
        loadMessages(message.conversation_id);
      },
    };
    showNotification(
      config.message,
      description,
      uzdLogo,
      {
        tag: config.key,
        onClick: config.onClick,
      },
    );
    if (priority === 5) notification.error(config);
    else if (priority === 4) notification.warning(config);
    else notification.info(config);
  }, [currentUserId, loadMessages, notification]);

  useEffect(() => {
    if (!socket) return;

    const onMessage = payload => {
      const message = payload?.data;
      if (!message?.conversation_id) return;
      mergeMessages(message.conversation_id, [message]);
      const incoming = message.sender_type === "system" || Number(message.sender_user_id) !== currentUserId;
      if (incoming) markDeliveredThrough(message.conversation_id, [message]);
      showIncomingNotification(message);
      scheduleConversationRefresh();
    };
    const onDelivered = payload => {
      const data = payload?.data;
      if (!data?.conversation_id) return;
      setMessagesByConversation(previous => ({
        ...previous,
        [data.conversation_id]: applyReceipt(previous[data.conversation_id] || [], {
          throughMessageId: data.through_message_id,
          field: "delivered_at",
          timestamp: data.delivered_at,
          currentUserId,
        }),
      }));
      scheduleConversationRefresh();
    };
    const onRead = payload => {
      const data = payload?.data;
      if (!data?.conversation_id) return;
      setMessagesByConversation(previous => ({
        ...previous,
        [data.conversation_id]: applyReceipt(previous[data.conversation_id] || [], {
          throughMessageId: data.through_message_id,
          field: "read_at",
          timestamp: data.read_at,
          currentUserId,
        }),
      }));
      scheduleConversationRefresh();
    };
    const onPresence = payload => {
      const data = payload?.data;
      if (!data?.user_id) return;
      setUsers(previous => previous.map(user => Number(user.id) === Number(data.user_id)
        ? { ...user, is_online: !!data.is_online, internal_chat_last_seen_at: data.last_seen_at }
        : user));
      setConversations(previous => previous.map(conversation => Number(conversation.other_user?.id) === Number(data.user_id)
        ? { ...conversation, other_user: { ...conversation.other_user, is_online: !!data.is_online, last_seen_at: data.last_seen_at } }
        : conversation));
    };
    const onAccessChanged = payload => {
      const data = payload?.data;
      if (!data?.user_id) return;
      if (Number(data.user_id) === currentUserId) {
        if (!data.enabled) disableChat();
        else refreshUsers().then(hasAccess => { if (hasAccess) { refreshConversations(); syncMissedMessages(); } });
        return;
      }
      setUsers(previous => previous.map(user => Number(user.id) === Number(data.user_id)
        ? { ...user, internal_chat_enabled: data.enabled ? 1 : 0 }
        : user));
      scheduleConversationRefresh();
    };

    socket.on("internal_chat:message", onMessage);
    socket.on("internal_chat:delivered", onDelivered);
    socket.on("internal_chat:read", onRead);
    socket.on("internal_chat:presence", onPresence);
    socket.on("internal_chat:access_changed", onAccessChanged);
    return () => {
      socket.off("internal_chat:message", onMessage);
      socket.off("internal_chat:delivered", onDelivered);
      socket.off("internal_chat:read", onRead);
      socket.off("internal_chat:presence", onPresence);
      socket.off("internal_chat:access_changed", onAccessChanged);
    };
  }, [currentUserId, disableChat, markDeliveredThrough, mergeMessages, refreshConversations, refreshUsers, scheduleConversationRefresh, showIncomingNotification, socket, syncMissedMessages]);

  const markSelectedRead = useCallback(() => {
    const conversationId = selectedRef.current;
    if (!openRef.current || !conversationId || document.visibilityState !== "visible") return;
    const messages = messagesRef.current[conversationId] || [];
    let through = highestIncomingMessageId(messages, currentUserId);
    const conversation = conversationsRef.current.find(row => String(row.id) === String(conversationId));
    if (!through && conversation?.last_message && (
      conversation.last_message.sender_type === "system"
      || Number(conversation.last_message.sender_user_id) !== currentUserId
    )) through = Number(conversation.last_message.id);
    if (!through) return;
    const previous = Number(readWatermarkRef.current[conversationId] || 0);
    if (through <= previous) return;
    readWatermarkRef.current[conversationId] = through;
    markConversationRead(conversationId, through)
      .then(() => refreshConversations())
      .catch(error => {
        readWatermarkRef.current[conversationId] = previous;
        handleApiError(error);
      });
  }, [currentUserId, handleApiError, refreshConversations]);

  useEffect(() => {
    const onVisibility = () => markSelectedRead();
    document.addEventListener("visibilitychange", onVisibility);
    const timer = setTimeout(markSelectedRead, 80);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [isOpen, selectedConversationId, messagesByConversation, markSelectedRead]);

  const openConversation = useCallback(async conversationId => {
    setIsOpen(true);
    setSelectedConversationId(conversationId);
    if (!messageMeta[conversationId]?.initialized) await loadMessages(conversationId);
  }, [loadMessages, messageMeta]);

  const startDirectConversation = useCallback(async user => {
    try {
      const direct = await ensureDirectConversation(user.id);
      const rows = await refreshConversations();
      if (!rows.some(row => String(row.id) === String(direct.id))) {
        setConversations(previous => sortConversations([...previous, {
          id: direct.id,
          type: "direct",
          other_user: {
            id: user.id,
            username: user.username,
            name: user.name,
            surname: user.surname,
            pfp: user.pfp,
            is_online: !!user.is_online,
            last_seen_at: user.internal_chat_last_seen_at,
          },
          last_message: null,
          unread_count: 0,
          last_message_at: null,
        }]));
      }
      await openConversation(direct.id);
      return direct;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [handleApiError, openConversation, refreshConversations]);

  const updateOptimistic = useCallback((conversationId, localMessageId, patch) => {
    setMessagesByConversation(previous => ({
      ...previous,
      [conversationId]: (previous[conversationId] || []).map(message => message.local_message_id === localMessageId
        ? { ...message, ...patch }
        : message),
    }));
  }, []);

  const stageMessage = useCallback(({ conversationId, text, priority = 3, quotedMessage = null, file = null, localMessageId = null, retryFileId = null, queued = false, createdAt = null }) => {
    const conversation = conversationsRef.current.find(row => String(row.id) === String(conversationId));
    if (!conversation || conversation.type === "system") return null;
    const uuid = localMessageId || uuidv4();
    const caption = String(text || "").trim() || null;
    const mediaType = file?.type?.startsWith("image/") ? "image" : file ? "document" : "text";
    const existing = (messagesRef.current[conversationId] || []).find(message => message.local_message_id === uuid);
    const optimistic = {
      ...(existing || {}),
      id: existing?.id || `local:${uuid}`,
      conversation_id: conversationId,
      local_message_id: uuid,
      sender_type: "user",
      sender_user_id: currentUserId,
      recipient_user_id: conversation.other_user?.id,
      message_type: existing?.message_type || mediaType,
      text_content: caption,
      priority: Number(priority),
      quoted_message_id: quotedMessage?.id || existing?.quoted_message_id || null,
      quoted_message: quotedMessage || existing?.quoted_message || null,
      created_at: existing?.created_at || createdAt || new Date().toISOString(),
      file: existing?.file || (file ? {
        id: null,
        original_name: file.name,
        mime_type: file.type,
        media_type: mediaType,
        size_bytes: file.size,
      } : null),
      _local_url: existing?._local_url || (file && mediaType === "image" ? URL.createObjectURL(file) : null),
      _pending_file: file || existing?._pending_file || null,
      _file_id: retryFileId || existing?._file_id || null,
      _status: queued ? "queued" : file && !retryFileId ? "uploading" : "sending",
      _upload_progress: file && !retryFileId ? 0 : existing?._upload_progress,
      _error: null,
    };
    mergeMessages(conversationId, [optimistic]);
    return { localMessageId: uuid, optimistic };
  }, [currentUserId, mergeMessages]);

  const sendMessage = useCallback(async ({ conversationId, text, priority = 3, quotedMessage = null, file = null, localMessageId = null, retryFileId = null }) => {
    const staged = stageMessage({ conversationId, text, priority, quotedMessage, file, localMessageId, retryFileId });
    if (!staged) return null;
    const { localMessageId: uuid, optimistic } = staged;
    const caption = String(text || "").trim() || null;

    try {
      let fileId = optimistic._file_id;
      if (file && !fileId) {
        const initiated = await initiateInternalUpload(file);
        fileId = initiated.upload?.id;
        updateOptimistic(conversationId, uuid, { _file_id: fileId });
        await putSignedUpload({
          uploadUrl: initiated.upload_url,
          requiredHeaders: initiated.required_headers,
          file,
          onProgress: progress => updateOptimistic(conversationId, uuid, { _upload_progress: progress }),
        });
        await completeInternalUpload(fileId);
        updateOptimistic(conversationId, uuid, { _status: "sending", _upload_progress: 100 });
      }
      const payload = {
        local_message_id: uuid,
        message_type: optimistic.message_type,
        text_content: caption,
        priority: Number(priority),
        quoted_message_id: optimistic.quoted_message_id,
        ...(fileId ? { file_id: fileId } : {}),
      };
      const canonical = await sendInternalMessage(conversationId, payload);
      mergeMessages(conversationId, [canonical]);
      scheduleConversationRefresh();
      return canonical;
    } catch (error) {
      handleApiError(error);
      updateOptimistic(conversationId, uuid, {
        _status: "failed",
        _error: error?.response?.data?.message || error.message || "Message failed to send.",
      });
      return null;
    }
  }, [handleApiError, mergeMessages, scheduleConversationRefresh, stageMessage, updateOptimistic]);

  const retryMessage = useCallback(message => sendMessage({
    conversationId: message.conversation_id,
    text: message.text_content,
    priority: message.priority,
    quotedMessage: message.quoted_message,
    file: message._pending_file,
    localMessageId: message.local_message_id,
    retryFileId: message._file_id,
  }), [sendMessage]);

  const resolveDownloadUrl = useCallback(async fileId => {
    const cached = downloadUrls[fileId];
    if (cached && Date.parse(cached.expires_at) - Date.now() > 30000) return cached.download_url;
    try {
      const data = await getInternalFileDownload(fileId);
      setDownloadUrls(previous => ({ ...previous, [fileId]: data }));
      return data.download_url;
    } catch (error) {
      handleApiError(error);
      throw error;
    }
  }, [downloadUrls, handleApiError]);

  const eligibleUsers = useMemo(() => users
    .filter(user => Number(user.id) !== currentUserId && Number(user.approved) === 1 && Number(user.internal_chat_enabled) === 1)
    .sort((a, b) => Number(!!b.is_online) - Number(!!a.is_online) || userDisplayName(a).localeCompare(userDisplayName(b))),
  [currentUserId, users]);

  const selectedConversation = useMemo(() => conversations.find(row => String(row.id) === String(selectedConversationId)) || null,
    [conversations, selectedConversationId]);

  const value = useMemo(() => ({
    enabled,
    initializing,
    syncing,
    isConnected,
    isOpen,
    setIsOpen,
    toggleOpen: () => setIsOpen(open => !open),
    users: eligibleUsers,
    conversations,
    totalUnread: unreadMessageTotal(conversations),
    selectedConversationId,
    selectedConversation,
    messages: selectedConversationId ? (messagesByConversation[selectedConversationId] || []) : [],
    messageMeta: selectedConversationId ? (messageMeta[selectedConversationId] || {}) : {},
    openConversation,
    startDirectConversation,
    loadOlderMessages: () => selectedConversationId && loadMessages(selectedConversationId, { older: true }),
    refreshConversations,
    stageMessage,
    sendMessage,
    retryMessage,
    resolveDownloadUrl,
    conversationTitle,
  }), [
    conversations, eligibleUsers, enabled, initializing, isConnected, isOpen, loadMessages, messageMeta,
    messagesByConversation, openConversation, refreshConversations, resolveDownloadUrl, retryMessage,
    selectedConversation, selectedConversationId, sendMessage, stageMessage, startDirectConversation, syncing,
  ]);

  return <InternalChatContext.Provider value={value}>{children}</InternalChatContext.Provider>;
}
